// --- React ---
import React, { useMemo, useState, useCallback } from 'react';

// --- 라우팅 ---
import { useNavigate } from 'react-router-dom';

// --- 다국어 ---
import i18n from 'i18next';

// --- 스타일 · 테마 ---
import { css } from '@emotion/react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { useAuthStore } from '@/store/authStore';
import { useSelectionStore } from '@/store/selectionStore';
import {
  useDashboardStatic,
  useDashboardAlarmHistory,
  useDashboardLiveAggregation,
  fetchDashboardReport,
} from '@/api/dashboard/useDashboard';
import { parseMonitoringApiError } from '@/utils/errors';
import { useTheme, breakpoints } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { TestSummary } from '@/components/ui/common/TestSummary';
import { DeviceSummary } from '@/components/ui/common/DeviceSummary';
import { AlarmSummary } from '@/components/ui/common/AlarmSummary';
import { DashboardBanner } from '@/components/ui/common/DashboardBanner';
import { Button, Flex } from '@/components/ui';
import { Text } from '@/components/ui/common/Typography';
import { getDeviceDisplayName } from '@packages/types/app';
import type {
  ReportDueDevice,
  ReportDeviceInfo,
  ReportAlarmDevice,
  ReportAlertDevice,
} from '@packages/types/dashboard';

// --- 타입 ---
import { isAdminByRoleId } from '@packages/types/auth/roles';

// ==================== 컴포넌트 ====================

const DashboardPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { selectedCompanyId, selectedSiteId } = useSelectionStore();

  // 카드 클릭 시 이동: Device Status → 관리(Site 탭), Online/Alarm → 실시간 모니터링
  const goToSiteManagement = useCallback(() => navigate('/management?tab=site'), [navigate]);
  const goToRealtimeMonitoring = useCallback(() => navigate('/monitoring/realtime'), [navigate]);

  // ==================== 파생 데이터 (권한 · 필터) ====================

  const isAdmin = isAdminByRoleId(user?.role_id);

  // Admin: selectedCompanyId + selectedSiteId 모두 사용
  // CompanyManager/SiteManager: selectedSiteId만 사용
  const companyId: number | null =
    isAdmin && selectedCompanyId && selectedCompanyId !== 'all'
      ? (selectedCompanyId as number)
      : null;

  const siteId: number | null =
    selectedSiteId && selectedSiteId !== 'all' ? (selectedSiteId as number) : null;

  // ==================== API 훅 ====================

  // Tier 1 — 5초 폴링: 백엔드 live-aggregation (monitoring/latest 기반 집계)
  const { data: liveAggregation, error: liveAggregationError } = useDashboardLiveAggregation({
    companyId: isAdmin ? (companyId ?? undefined) : undefined,
    siteId: siteId ?? undefined,
  });

  // API가 일부 필드만 줄 때도 Object.keys(undefined) 등으로 크래시 나지 않게 기본값 병합
  const live = {
    deviceStatus: liveAggregation?.deviceStatus ?? {},
    liveAlarms: liveAggregation?.liveAlarms ?? {},
    liveEvents: liveAggregation?.liveEvents ?? {},
  };

  // Tier 2 — 60초 폴링 (in10Min)
  const {
    data: alarmHistoryRes,
    isLoading: ahLoading,
    error: alarmHistoryError,
  } = useDashboardAlarmHistory({
    companyId: isAdmin ? (companyId ?? undefined) : undefined,
    siteId: siteId ?? undefined,
  });

  // Tier 3 — 5분 폴링 (total, bumpCal)
  const {
    data: staticRes,
    isLoading: staticLoading,
    error: staticError,
  } = useDashboardStatic({
    companyId: isAdmin ? (companyId ?? undefined) : undefined,
    siteId: siteId ?? undefined,
  });

  /**
   * 3개 API 에러를 하나로 병합.
   * 어느 Tier에서든 에러 발생 시 첫 번째 에러만 표시한다.
   */
  const dashboardApiError = useMemo(() => {
    const raw = liveAggregationError ?? staticError ?? alarmHistoryError;
    return raw ? parseMonitoringApiError(raw) : null;
  }, [liveAggregationError, staticError, alarmHistoryError]);

  // ==================== 파생 데이터 (집계 조립) ====================

  /**
   * 디바이스 상태 집계.
   * static(Tier 3)의 total + live(Tier 1)의 connected/alarms/alerts를 사이트별로 병합.
   */
  const deviceStatus = useMemo(() => {
    if (!staticRes) return { data: {}, loading: staticLoading };
    const data: Record<string, any> = {};
    const rows = staticRes.deviceStatus?.data ?? {};
    for (const [key, stat] of Object.entries(rows)) {
      const liveSite = live.deviceStatus[key] ?? { connected: 0, alarms: 0, alerts: 0 };
      data[key] = {
        total: stat?.total ?? 0,
        connected: liveSite.connected,
        alarms: liveSite.alarms,
        alerts: liveSite.alerts,
      };
    }
    return { data, loading: false };
  }, [staticRes, live.deviceStatus, staticLoading]);

  /** Bump/Cal 만기 집계 (Tier 3) */
  const bumpCal = useMemo(() => {
    if (!staticRes) return { data: { bump: {}, cal: {} }, loading: staticLoading };
    return { data: staticRes.bumpCal?.data ?? { bump: {}, cal: {} }, loading: false };
  }, [staticRes, staticLoading]);

  /**
   * 실시간 알람 집계.
   * alarmHistory(Tier 2, in10Min) + live(Tier 1, now)를 사이트별로 병합.
   */
  const liveAlarms = useMemo(() => {
    const base = alarmHistoryRes?.liveAlarms?.data ?? {};
    const data: Record<string, any> = {};
    const liveNow = live.liveAlarms ?? {};
    const allKeys = new Set([...Object.keys(base), ...Object.keys(liveNow)]);
    for (const key of allKeys) {
      const h = base[key] ?? {
        twa: { in10Min: 0 },
        stel: { in10Min: 0 },
        low: { in10Min: 0 },
        high: { in10Min: 0 },
      };
      const n = liveNow[key] ?? {
        twa: { now: 0 },
        stel: { now: 0 },
        low: { now: 0 },
        high: { now: 0 },
      };
      data[key] = {
        twa: { in10Min: h.twa?.in10Min ?? 0, now: n.twa?.now ?? 0 },
        stel: { in10Min: h.stel?.in10Min ?? 0, now: n.stel?.now ?? 0 },
        low: { in10Min: h.low?.in10Min ?? 0, now: n.low?.now ?? 0 },
        high: { in10Min: h.high?.in10Min ?? 0, now: n.high?.now ?? 0 },
      };
    }
    return { data, loading: ahLoading };
  }, [alarmHistoryRes, live.liveAlarms, ahLoading]);

  /**
   * 표시할 사이트 키 결정.
   * siteId 선택 → 해당 ID, 미선택 → 'total', 없으면 첫 번째 키.
   */
  const getDisplayKey = (dataObj: Record<string, any>) => {
    if (Object.keys(dataObj).length === 0) return null;

    // siteId가 선택되어 있으면 해당 키 사용 (API 키는 문자열, persist된 siteId는 문자일 수 있음)
    const siteKey = siteId != null ? String(siteId) : null;
    if (siteKey && dataObj[siteKey] != null) {
      return siteKey;
    }

    // 'all' 선택 또는 siteId 없으면 'total' 키 사용
    if (dataObj['total']) {
      return 'total';
    }

    // 그 외의 경우 첫 번째 키 사용
    return Object.keys(dataObj)[0];
  };

  const deviceStatusKey = getDisplayKey(deviceStatus.data);
  const liveAlarmsKey = getDisplayKey(liveAlarms.data);
  const bumpCalBumpKey = getDisplayKey(bumpCal.data.bump || {});
  const bumpCalCalKey = getDisplayKey(bumpCal.data.cal || {});

  // 알람이 있는 디바이스 수 계산
  const alarmDeviceCount =
    deviceStatusKey && deviceStatus.data[deviceStatusKey]
      ? deviceStatus.data[deviceStatusKey].alarms || 0
      : 0;

  // ==================== Report Download ====================
  const [reportLoading, setReportLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    return fetchDashboardReport(
      isAdmin ? (companyId ?? undefined) : undefined,
      siteId ?? undefined
    );
  }, [isAdmin, companyId, siteId]);

  // ── Excel row formatters ──

  const fmtDueRow = (d: ReportDueDevice) => ({
    'Serial Number': d.serial_number,
    'Device Type': getDeviceDisplayName(d.device_type, d.app_id),
    Site: d.site_name ?? '',
    'Due Date': d.due_date ?? '',
    Days: d.days ?? '',
  });

  const fmtDeviceRow = (d: ReportDeviceInfo) => ({
    'Serial Number': d.serial_number,
    'Device Type': getDeviceDisplayName(d.device_type, d.app_id),
    Site: d.site_name ?? '',
    Company: d.company_name ?? '',
    'Device Name': d.cloud_name ?? '',
    Worker: d.worker_name ?? '',
    Sensors: d.sensor_types.join(', '),
    Status: d.is_connected ? 'Online' : 'Offline',
    'Last Connect': d.last_connect ? new Date(d.last_connect).toLocaleString() : '',
    'Cal Due Date': d.calibration_due_date ?? '',
    'Cal Days': d.cal_due_day_num ?? '',
    'Bump Due Date': d.bump_test_due_date ?? '',
    'Bump Days': d.bump_due_day_num ?? '',
    FW: d.fw_version ?? '',
    HW: d.hw_version ?? '',
  });

  const fmtAlarmRow = (d: ReportAlarmDevice) => ({
    'Serial Number': d.serial_number,
    'Device Type': getDeviceDisplayName(d.device_type, d.app_id),
    Site: d.site_name ?? '',
    'Alarm Type(s)': d.alarm_types.join(', '),
    Timestamp: d.timestamp ? new Date(d.timestamp).toLocaleString() : '',
  });

  const fmtAlertRow = (d: ReportAlertDevice) => ({
    'Serial Number': d.serial_number,
    'Device Type': getDeviceDisplayName(d.device_type, d.app_id),
    Site: d.site_name ?? '',
    'Alert Type(s)': d.alert_types.join(', '),
    Timestamp: d.timestamp ? new Date(d.timestamp).toLocaleString() : '',
  });

  const handleExcelDownload = useCallback(async () => {
    try {
      setReportLoading(true);
      const r = await fetchReport();
      const wb = XLSX.utils.book_new();

      const addSheet = (name: string, rows: any[]) => {
        const sheetRows = rows.length > 0 ? rows : [{ Status: 'No data' }];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), name);
      };

      const infoRows: Record<string, string>[] = [
        { '': 'Generated', ' ': new Date(r.generatedAt).toLocaleString('en-US') },
      ];
      if (r.companyName) infoRows.push({ '': 'Company', ' ': r.companyName });
      if (r.siteName) infoRows.push({ '': 'Site', ' ': r.siteName });
      addSheet('Info', infoRows);

      addSheet('Total Devices', r.online.total.map(fmtDeviceRow));
      addSheet('Cal Overdue', r.calibration.overdue.map(fmtDueRow));
      addSheet('Cal Due 30 Days', r.calibration.dueIn30Days.map(fmtDueRow));
      addSheet('Bump Overdue', r.bumpTest.overdue.map(fmtDueRow));
      addSheet('Bump Due 30 Days', r.bumpTest.dueIn30Days.map(fmtDueRow));
      addSheet('Connected', r.online.connected.map(fmtDeviceRow));
      addSheet('Alarms', r.online.alarms.map(fmtAlarmRow));
      addSheet('Alerts', r.online.alerts.map(fmtAlertRow));

      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Dashboard_Report_${dateStr}.xlsx`);
    } catch (error) {
      console.error('Excel download failed:', error);
    } finally {
      setReportLoading(false);
    }
  }, [fetchReport]);

  const handlePdfDownload = useCallback(async () => {
    try {
      setReportLoading(true);
      const r = await fetchReport();
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const rowHeight = 5.5;
      const headerRowHeight = 6.5;
      let y = margin;

      type ColDef = { key: string; header: string; width: number };

      const scaleColumns = (cols: ColDef[]) => {
        const total = cols.reduce((s, c) => s + c.width, 0);
        const ratio = contentWidth / total;
        return cols.map(c => ({ ...c, width: c.width * ratio }));
      };

      const ensureSpace = (needed: number) => {
        if (y + needed > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
      };

      const drawTableHeader = (cols: ColDef[]) => {
        ensureSpace(headerRowHeight + rowHeight);
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, y, contentWidth, headerRowHeight, 'F');
        pdf.setDrawColor(100, 100, 100);
        pdf.setLineWidth(0.3);
        pdf.line(margin, y + headerRowHeight, margin + contentWidth, y + headerRowHeight);
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        let x = margin;
        cols.forEach(col => {
          pdf.text(col.header, x + 1, y + 4.5, { maxWidth: col.width - 2 });
          x += col.width;
        });
        y += headerRowHeight + 0.5;
      };

      const drawRow = (cols: ColDef[], values: Record<string, string>, highlight = false) => {
        if (y + rowHeight > pageHeight - margin) {
          pdf.addPage();
          y = margin;
          drawTableHeader(cols);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(5.5);
        }
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.1);
        pdf.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);
        pdf.setTextColor(highlight ? 200 : 0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(5.5);
        let x = margin;
        cols.forEach(col => {
          pdf.text(values[col.key] ?? '', x + 1, y + 4, { maxWidth: col.width - 2 });
          x += col.width;
        });
        y += rowHeight;
      };

      const formatDate = (d: string | Date) => {
        const date = typeof d === 'string' ? new Date(d) : d;
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      };

      // ── Summary card 그리기 ──
      const cardW = 50;
      const cardH = 18;
      const cardGap = 4;

      const drawSummaryCard = (
        x: number,
        label: string,
        value: number,
        variant: 'default' | 'danger' | 'warning' = 'default'
      ) => {
        const bgColor: [number, number, number] =
          variant === 'danger'
            ? [255, 235, 235]
            : variant === 'warning'
              ? [255, 248, 230]
              : [245, 245, 245];
        const textColor: [number, number, number] =
          variant === 'danger' ? [200, 0, 0] : variant === 'warning' ? [180, 120, 0] : [60, 60, 60];

        pdf.setFillColor(...bgColor);
        pdf.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(label, x + cardW / 2, y + 6, { align: 'center' });
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...textColor);
        pdf.text(String(value), x + cardW / 2, y + 14, { align: 'center' });
      };

      // ── Subsection title ──
      const drawSubTitle = (title: string, count?: number) => {
        ensureSpace(14);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        const text = count != null ? `${title} (${count})` : title;
        pdf.text(text, margin, y + 4);
        y += 8;
      };

      const dueValues = (d: ReportDueDevice): Record<string, string> => ({
        sn: d.serial_number,
        type: getDeviceDisplayName(d.device_type, d.app_id),
        site: d.site_name ?? '',
        due: d.due_date ?? '',
        days: d.days != null ? String(d.days) : '',
      });

      const infoValues = (d: ReportDeviceInfo): Record<string, string> => ({
        sn: d.serial_number,
        type: getDeviceDisplayName(d.device_type, d.app_id),
        site: d.site_name ?? '',
        name: d.cloud_name ?? '',
        sensors: d.sensor_types.join(', '),
        status: d.is_connected ? 'Online' : 'Offline',
        lastConn: d.last_connect ? formatDate(d.last_connect) : '',
        calDue: d.calibration_due_date ?? '',
        calDays: d.cal_due_day_num != null ? String(d.cal_due_day_num) : '',
        bumpDue: d.bump_test_due_date ?? '',
        bumpDays: d.bump_due_day_num != null ? String(d.bump_due_day_num) : '',
        fw: d.fw_version ?? '',
      });

      const dueCols = scaleColumns([
        { key: 'sn', header: 'Serial Number', width: 40 },
        { key: 'type', header: 'Device Type', width: 25 },
        { key: 'site', header: 'Site', width: 40 },
        { key: 'due', header: 'Due Date', width: 30 },
        { key: 'days', header: 'Days', width: 20 },
      ]);

      const infoCols = scaleColumns([
        { key: 'sn', header: 'S/N', width: 22 },
        { key: 'type', header: 'Type', width: 16 },
        { key: 'site', header: 'Site', width: 28 },
        { key: 'name', header: 'Name', width: 25 },
        { key: 'sensors', header: 'Sensors', width: 28 },
        { key: 'status', header: 'Status', width: 14 },
        { key: 'lastConn', header: 'Last Connect', width: 30 },
        { key: 'calDue', header: 'Cal Due', width: 20 },
        { key: 'calDays', header: 'Cal Days', width: 14 },
        { key: 'bumpDue', header: 'Bump Due', width: 20 },
        { key: 'bumpDays', header: 'Bump Days', width: 14 },
        { key: 'fw', header: 'FW', width: 16 },
      ]);

      const alarmCols = scaleColumns([
        { key: 'sn', header: 'Serial Number', width: 35 },
        { key: 'type', header: 'Device Type', width: 22 },
        { key: 'site', header: 'Site', width: 35 },
        { key: 'alarms', header: 'Alarm Type(s)', width: 45 },
        { key: 'ts', header: 'Timestamp', width: 30 },
      ]);

      const alertCols = scaleColumns([
        { key: 'sn', header: 'Serial Number', width: 35 },
        { key: 'type', header: 'Device Type', width: 22 },
        { key: 'site', header: 'Site', width: 35 },
        { key: 'alerts', header: 'Alert Type(s)', width: 45 },
        { key: 'ts', header: 'Timestamp', width: 30 },
      ]);

      // ════════════════════════════════════════════
      // Page header
      // ════════════════════════════════════════════
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Dashboard Report', margin, y);

      const headerItems: { label: string; value: string }[] = [
        { label: 'Generated:', value: formatDate(r.generatedAt) },
      ];
      if (r.companyName) headerItems.push({ label: 'Company:', value: r.companyName });
      if (r.siteName) headerItems.push({ label: 'Site:', value: r.siteName });

      const headerFontSize = 9;
      const headerLineHeight = 5;
      pdf.setFontSize(headerFontSize);
      pdf.setFont('helvetica', 'bold');
      const maxLabelW = Math.max(...headerItems.map(h => pdf.getTextWidth(h.label)));
      pdf.setFont('helvetica', 'normal');
      const maxValueW = Math.max(...headerItems.map(h => pdf.getTextWidth(`  ${h.value}`)));
      const blockW = maxLabelW + maxValueW;
      const blockX = pageWidth - margin - blockW;
      const headerStartY = y - 4;

      headerItems.forEach((item, i) => {
        const ly = headerStartY + i * headerLineHeight;
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(80, 80, 80);
        pdf.text(item.label, blockX, ly);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.text(`  ${item.value}`, blockX + maxLabelW, ly);
      });

      y += Math.max(7, headerItems.length * headerLineHeight) + 4;

      const totalN = r.online.total.length;
      const connN = r.online.connected.length;
      const alarmN = r.online.alarms.length;
      const alertN = r.online.alerts.length;
      const calOverdueN = r.calibration.overdue.length;
      const calDue30N = r.calibration.dueIn30Days.length;
      const bumpOverdueN = r.bumpTest.overdue.length;
      const bumpDue30N = r.bumpTest.dueIn30Days.length;

      // ════════════════════════════════════════════
      // 1. Summary Cards — Overview
      // ════════════════════════════════════════════
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Overview', margin, y + 4);
      y += 10;

      let cx = margin;
      drawSummaryCard(cx, 'Total', totalN);
      cx += cardW + cardGap;
      drawSummaryCard(cx, 'Connected', connN);
      cx += cardW + cardGap;
      drawSummaryCard(cx, 'Cal Overdue', calOverdueN, calOverdueN > 0 ? 'danger' : 'default');
      cx += cardW + cardGap;
      drawSummaryCard(cx, 'Bump Overdue', bumpOverdueN, bumpOverdueN > 0 ? 'danger' : 'default');
      cx += cardW + cardGap;
      drawSummaryCard(cx, 'Alarms', alarmN, alarmN > 0 ? 'danger' : 'default');
      y += cardH + 6;

      // ════════════════════════════════════════════
      // 2. Total Device List (first)
      // ════════════════════════════════════════════
      drawSubTitle('Total Devices', totalN);
      drawTableHeader(infoCols);
      r.online.total.forEach(d => {
        const isOverdue =
          (d.cal_due_day_num != null && d.cal_due_day_num < 0) ||
          (d.bump_due_day_num != null && d.bump_due_day_num < 0);
        drawRow(infoCols, infoValues(d), isOverdue);
      });
      y += 6;

      // ════════════════════════════════════════════
      // 3. Calibration
      // ════════════════════════════════════════════
      if (calOverdueN > 0) {
        drawSubTitle('Calibration Overdue', calOverdueN);
        drawTableHeader(dueCols);
        r.calibration.overdue.forEach(d => drawRow(dueCols, dueValues(d), true));
        y += 4;
      }

      if (calDue30N > 0) {
        drawSubTitle('Calibration Due in 30 Days', calDue30N);
        drawTableHeader(dueCols);
        r.calibration.dueIn30Days.forEach(d => drawRow(dueCols, dueValues(d)));
        y += 4;
      }

      // ════════════════════════════════════════════
      // 4. Bump Test
      // ════════════════════════════════════════════
      if (bumpOverdueN > 0) {
        drawSubTitle('Bump Test Overdue', bumpOverdueN);
        drawTableHeader(dueCols);
        r.bumpTest.overdue.forEach(d => drawRow(dueCols, dueValues(d), true));
        y += 4;
      }

      if (bumpDue30N > 0) {
        drawSubTitle('Bump Test Due in 30 Days', bumpDue30N);
        drawTableHeader(dueCols);
        r.bumpTest.dueIn30Days.forEach(d => drawRow(dueCols, dueValues(d)));
        y += 4;
      }

      // ════════════════════════════════════════════
      // 5. Connected
      // ════════════════════════════════════════════
      if (connN > 0) {
        drawSubTitle('Connected Devices', connN);
        drawTableHeader(infoCols);
        r.online.connected.forEach(d => drawRow(infoCols, infoValues(d)));
        y += 4;
      }

      // ════════════════════════════════════════════
      // 6. Alarms
      // ════════════════════════════════════════════
      if (alarmN > 0) {
        drawSubTitle('Alarms', alarmN);
        drawTableHeader(alarmCols);
        r.online.alarms.forEach(d =>
          drawRow(
            alarmCols,
            {
              sn: d.serial_number,
              type: getDeviceDisplayName(d.device_type, d.app_id),
              site: d.site_name ?? '',
              alarms: d.alarm_types.join(', '),
              ts: d.timestamp ? formatDate(d.timestamp) : '',
            },
            true
          )
        );
        y += 4;
      }

      // ════════════════════════════════════════════
      // 7. Alerts
      // ════════════════════════════════════════════
      if (alertN > 0) {
        drawSubTitle('Alerts', alertN);
        drawTableHeader(alertCols);
        r.online.alerts.forEach(d =>
          drawRow(alertCols, {
            sn: d.serial_number,
            type: getDeviceDisplayName(d.device_type, d.app_id),
            site: d.site_name ?? '',
            alerts: d.alert_types.join(', '),
            ts: d.timestamp ? formatDate(d.timestamp) : '',
          })
        );
      }

      pdf.setTextColor(0, 0, 0);
      const dateStr = new Date().toISOString().split('T')[0];
      pdf.save(`Dashboard_Report_${dateStr}.pdf`);
    } catch (error) {
      console.error('PDF download failed:', error);
    } finally {
      setReportLoading(false);
    }
  }, [fetchReport]);

  // ==================== 렌더링 ====================

  return (
    <div style={wrapStyle}>
      {/* API 에러 배너 */}
      {dashboardApiError && (
        <Text
          size="sm"
          weight="medium"
          color={theme.colors.status.danger.stroke.text}
          align="left"
          style={{ width: '100%' }}
        >
          {i18n.t(dashboardApiError.title)}
          {dashboardApiError.message && dashboardApiError.message !== dashboardApiError.title
            ? ` — ${i18n.t(dashboardApiError.message)}`
            : ''}
        </Text>
      )}

      {/* 대시보드 배너 — 알람 디바이스 수 표시 */}
      <DashboardBanner deviceCount={alarmDeviceCount} type="alarm" />

      {/* Download Buttons */}
      <div
        data-tour="report-download"
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          margin-top: -20px;
          margin-bottom: -20px;
          flex-wrap: wrap;
          @media (max-width: ${breakpoints.sm}px) {
            margin-top: 0;
            margin-bottom: 0;
            justify-content: stretch;
            & > button {
              flex: 1 1 140px;
              width: auto !important;
            }
          }
        `}
      >
        <Button
          variant="primary"
          onClick={handleExcelDownload}
          disabled={reportLoading}
          style={{ width: '178px' }}
        >
          {reportLoading ? 'Loading...' : 'Download Excel'}
        </Button>
        <Button
          variant="primary"
          onClick={handlePdfDownload}
          disabled={reportLoading}
          style={{ width: '178px' }}
        >
          {reportLoading ? 'Loading...' : 'Download PDF'}
        </Button>
      </div>

      {/* Calibration · Bump Test 요약 카드 */}
      <div
        data-tour="device-status"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: `${theme.spacing[6]}px`,
        }}
      >
        <Text
          size="lg"
          weight="semibold"
          color="textPrimary"
          align="left"
          style={{ width: '100%', marginBottom: '-12px' }}
        >
          Device Status
        </Text>
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing[6]}px;
            @media (min-width: ${breakpoints.lg}) {
              flex-direction: row;
            }
          `}
        >
          {bumpCalCalKey && bumpCal.data.cal?.[bumpCalCalKey] && (
            <TestSummary
              title="Calibration"
              data={{
                total: bumpCal.data.cal[bumpCalCalKey].total || 0,
                next30Days: bumpCal.data.cal[bumpCalCalKey].next30Days || 0,
                overDue: bumpCal.data.cal[bumpCalCalKey].overDue || 0,
              }}
              onClick={goToSiteManagement}
            />
          )}
          {bumpCalBumpKey && bumpCal.data.bump?.[bumpCalBumpKey] && (
            <TestSummary
              title="Bump Test"
              data={{
                total: bumpCal.data.bump[bumpCalBumpKey].total || 0,
                next30Days: bumpCal.data.bump[bumpCalBumpKey].next30Days || 0,
                overDue: bumpCal.data.bump[bumpCalBumpKey].overDue || 0,
              }}
              onClick={goToSiteManagement}
            />
          )}
        </div>
      </div>

      {/* Online — 연결·알람 요약 */}
      <div
        data-tour="online-summary"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '28px',
        }}
      >
        <Text
          size="lg"
          weight="semibold"
          color="textPrimary"
          align="left"
          style={{ width: '100%', marginBottom: '-12px' }}
        >
          Online
        </Text>
        {deviceStatusKey && deviceStatus.data[deviceStatusKey] && (
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
              align-self: stretch;
              @media (max-width: ${breakpoints.sm}px) {
                grid-template-columns: repeat(2, 1fr);
              }
            `}
          >
            <DeviceSummary
              title="Total"
              value={deviceStatus.data[deviceStatusKey].total || 0}
              variant="default"
              showIcon={false}
              onClick={goToRealtimeMonitoring}
            />
            <DeviceSummary
              title="Connected"
              value={deviceStatus.data[deviceStatusKey].connected || 0}
              variant="default"
              showIcon={false}
              onClick={goToRealtimeMonitoring}
            />
            <DeviceSummary
              title="Alarms"
              value={deviceStatus.data[deviceStatusKey].alarms || 0}
              variant="danger"
              showIcon={deviceStatus.data[deviceStatusKey].alarms > 0}
              onClick={goToRealtimeMonitoring}
            />
            <DeviceSummary
              title="Alerts"
              value={deviceStatus.data[deviceStatusKey].alerts || 0}
              variant="caution"
              showIcon={deviceStatus.data[deviceStatusKey].alerts > 0}
              onClick={goToRealtimeMonitoring}
            />
          </div>
        )}

        {liveAlarmsKey && liveAlarms.data[liveAlarmsKey] && (
          <>
            <Flex direction="row" gap={6} style={{ width: '100%' }}>
              <AlarmSummary
                title="High Alarm"
                data={{
                  current: liveAlarms.data[liveAlarmsKey].high?.now || 0,
                  in10Min: liveAlarms.data[liveAlarmsKey].high?.in10Min || 0,
                }}
                variant="danger"
                onClick={goToRealtimeMonitoring}
              />
              <AlarmSummary
                title="Low Alarm"
                data={{
                  current: liveAlarms.data[liveAlarmsKey].low?.now || 0,
                  in10Min: liveAlarms.data[liveAlarmsKey].low?.in10Min || 0,
                }}
                variant="danger"
                onClick={goToRealtimeMonitoring}
              />
            </Flex>
            <Flex direction="row" gap={6} style={{ width: '100%' }}>
              <AlarmSummary
                title="TWA Alarm"
                data={{
                  current: liveAlarms.data[liveAlarmsKey].twa?.now || 0,
                  in10Min: liveAlarms.data[liveAlarmsKey].twa?.in10Min || 0,
                }}
                variant="danger"
                onClick={goToRealtimeMonitoring}
              />
              <AlarmSummary
                title="STEL Alarm"
                data={{
                  current: liveAlarms.data[liveAlarmsKey].stel?.now || 0,
                  in10Min: liveAlarms.data[liveAlarmsKey].stel?.in10Min || 0,
                }}
                variant="danger"
                onClick={goToRealtimeMonitoring}
              />
            </Flex>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== 스타일 ====================

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '28px',
  flex: '1 1 auto',
  alignSelf: 'stretch',
  padding: '24px',
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: 0,
};

export default DashboardPage;
